import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Popper,
    Paper,
    Grid,
    IconButton,
    Stack,
    ClickAwayListener,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import { Close } from '@mui/icons-material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import '../../assets/styles/DateRangePicker.scss';
import moment from 'moment';

export default function CommonDateRangePicker({ startDate, endDate, onChange }) {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedQuickFilter, setSelectedQuickFilter] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);

        // Determine selected quick filter on open
        if (startDate && endDate) {
            const currentStart = moment(startDate);
            const currentEnd = moment(endDate);
            let matchedIndex = null;

            quickFilters.forEach((preset, index) => {
                if (preset.getRange) {
                    const range = preset.getRange();
                    const presetStart = moment(range.startDate);
                    const presetEnd = moment(range.endDate);
                    if (currentStart.isSame(presetStart, 'day') && currentEnd.isSame(presetEnd, 'day')) {
                        matchedIndex = index;
                    }
                }
            });
            setSelectedQuickFilter(matchedIndex);
        } else {
            setSelectedQuickFilter(null);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatDateRange = () => {
        if (!startDate || !endDate) return '';
        return `${format(new Date(startDate), 'MM/dd/yyyy')} - ${format(new Date(endDate), 'MM/dd/yyyy')}`;
    };

    const quickFilters = [
        {
            label: 'Today',
            getRange: () => ({
                startDate: moment().startOf('day').toDate(),
                endDate: moment().endOf('day').toDate(),
            })
        },
        {
            label: 'Yesterday',
            getRange: () => ({
                startDate: moment().subtract(1, 'day').startOf('day').toDate(),
                endDate: moment().subtract(1, 'day').endOf('day').toDate(),
            })
        },
        {
            label: 'Last 7 Days',
            getRange: () => ({
                startDate: moment().subtract(6, 'days').startOf('day').toDate(),
                endDate: moment().endOf('day').toDate(),
            })
        },
        {
            label: 'Last 30 Days',
            getRange: () => ({
                startDate: moment().subtract(29, 'days').startOf('day').toDate(),
                endDate: moment().endOf('day').toDate(),
            })
        },
        {
            label: 'This Month',
            getRange: () => ({
                startDate: moment().startOf('month').toDate(),
                endDate: moment().endOf('month').toDate(),
            })
        },
        {
            label: 'Last Month',
            getRange: () => ({
                startDate: moment().subtract(1, 'month').startOf('month').toDate(),
                endDate: moment().subtract(1, 'month').endOf('month').toDate(),
            })
        },
        {
            label: 'Custom Range',
            getRange: null,
        },
    ];

    const handleQuickFilter = (preset, index) => {
        if (preset.label === 'Custom Range') {
            setSelectedQuickFilter(index);
            return;
        }
        const range = preset.getRange();
        setSelectedQuickFilter(index);
        onChange({
            startDate: range.startDate,
            endDate: range.endDate
        });
        setAnchorEl(null);
    };

    const handleManualDateChange = (ranges) => {
        const { startDate: newStart, endDate: newEnd } = ranges.selection;
        onChange({ startDate: newStart, endDate: newEnd });
        setSelectedQuickFilter(null);
    };

    const handleClearDateRange = () => {
        onChange({ startDate: null, endDate: null });
        handleClose();
    };

    return (
        <Box className="date-picker-wrapper" sx={{ width: '100%' }}>
            <TextField
                label="Date Range"
                value={formatDateRange()}
                onClick={handleClick}
                size="small"
                InputProps={{
                    readOnly: true,
                    endAdornment: startDate && endDate ? (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearDateRange();
                            }}
                            sx={{ mr: -1 }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    ) : null
                }}
                sx={{ width: '100%' }}
            />
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{
                    zIndex: 1500,
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '100vw', sm: '850px' },
                    left: { xs: 0, sm: 'auto' },
                    right: { xs: 0, sm: 'auto' },
                    px: { xs: 1, sm: 0 }
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 0,
                            background: '#fff',
                            width: { xs: '100%', sm: '750px' },
                            maxWidth: { xs: '100%', sm: '850px' },
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' }
                        }}
                    >
                        <Grid container sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                            {/* Left Side - Quick Filter Buttons (Vertical) */}
                            <Grid item xs={12} md={3} sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
                                borderBottom: { xs: '1px solid #e0e0e0', md: 'none' }
                            }}>
                                <Box sx={{
                                    p: 2,
                                    // minHeight: { xs: 'auto', md: '400px' },
                                    backgroundColor: '#fafafa',
                                    width: '100%',
                                    height: '100%'
                                }}>
                                    <Box sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                                        Quick Filters
                                    </Box>
                                    <Stack direction={{ xs: 'row', md: 'column' }} spacing={0.5} sx={{
                                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                                        overflowX: { xs: 'auto', md: 'visible' }
                                    }}>
                                        {quickFilters.map((preset, index) => (
                                            <Button
                                                key={preset.label}
                                                variant={selectedQuickFilter === index ? "contained" : "text"}
                                                size="small"
                                                onClick={() => handleQuickFilter(preset, index)}
                                                fullWidth={false}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem',
                                                    justifyContent: 'flex-start',
                                                    px: { xs: 1.5, md: 2 },
                                                    py: 1,
                                                    mb: { xs: 0, md: 0.5 },
                                                    mr: { xs: 0.5, md: 0 },
                                                    minWidth: { xs: 'auto', md: '100%' },
                                                    backgroundColor: selectedQuickFilter === index ? 'primary.main' : 'transparent',
                                                    color: selectedQuickFilter === index ? 'white' : 'text.primary',
                                                    '&:hover': {
                                                        backgroundColor: selectedQuickFilter === index ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
                                                    }
                                                }}
                                                className={`quick-filter-btn ${selectedQuickFilter === index ? 'active' : ''}`}
                                            >
                                                {preset.label}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                            </Grid>

                            {/* Right Side - Calendar */}
                            <Grid item xs={12} md={9} sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    p: { xs: 1, sm: 2 },
                                    overflow: 'auto',
                                    maxWidth: '100%'
                                }}>
                                    <DateRange
                                        onChange={handleManualDateChange}
                                        moveRangeOnFirstSelection={false}
                                        ranges={[{
                                            startDate: startDate || null,
                                            endDate: endDate || null,
                                            key: 'selection'
                                        }]}
                                        months={isTablet ? 1 : 2}
                                        direction="horizontal"
                                        showDateDisplay={false}
                                        showPreview={true}
                                        rangeColors={['#1976d2']}
                                    />

                                    {/* Action Buttons */}
                                    <Box display="flex" justifyContent="flex-end" gap={1} sx={{
                                        mt: 2,
                                        pt: 2,
                                        borderTop: '1px solid #e0e0e0',
                                        flexWrap: 'wrap'
                                    }}>
                                        <Button
                                            variant="outlined"
                                            size="medium"
                                            onClick={() => {
                                                handleClose(); // Just close, don't clear unless clear button used
                                            }}
                                            sx={{ textTransform: 'none', px: { xs: 2, sm: 3 } }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            onClick={() => {
                                                handleClose();
                                            }}
                                            sx={{ textTransform: 'none', px: { xs: 2, sm: 3 } }}
                                        >
                                            Apply
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}
